import { DateRange } from "@/hooks/useDateRange";
import { Room } from "./room";
import { Document } from "./document";
import { PreCheckStatus } from "@/components/pages/settlement/types/settlementBooking";
import { Request } from "./request";
import { BookingGuest } from "@/types/booking-guest";
import { GuestStatus, GuestType } from "@/types/guest";
import { BillingTransaction } from "./billing-transactions";

export enum ReservationStatus {
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  CHECKED_IN = "CHECKED_IN",
  CHECKED_OUT = "CHECKED_OUT",
  CANCELLED = "CANCELLED",
}

export { GuestStatus };

export enum CheckinStatus {
  DONE = "DONE",
  NOT_DONE = "NOT_DONE",
}

export enum CheckoutStatus {
  DONE = "DONE",
  NOT_DONE = "NOT_DONE",
}

export interface Guest {
  id: string;
  documents: Document[];
  profileId: string;
  profile: GuestProfile;
  primaryBookings?: Array<{
    checkinComments?: string | null;
  }>;
}

export interface GuestProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  address: string | null;
  language: string | null;
  preferences: Record<string, any> | null;
  preferredLanguage: string;
  hotelId: string | null;
  tenantId: string;
  dateOfBirth: string | null;
  phone: string | null;
  photoId: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface Reservation {
  id: string;
  reservationNumber: string;
  guest: Guest;
  room: Room;
  status: ReservationStatus;
  totalAmount?: number;
  notes?: string[];
  bookingSource?: string;
  createdAt: string;
  updatedAt: string;
  hotelId: string;
  arrivalDate: string;
  bookingNumber: number;
  checkInDate: Date;
  checkOutDate: Date;
  checkinComments: string;
  checkinMethod: string;
  checkinStatus: CheckinStatus;
  checkoutStatus: CheckoutStatus;
  preCheckinStatus: PreCheckStatus;
  preCheckoutStatus: PreCheckStatus;
  documents: Document[];

  departureDate: string;
  estimatedTimeOfArrival: string;
  preCheckinReadAt: string | null;
  preCheckinCompletedAt: string | null;
  guestId: string;
  preCheckinEnteredGuests: number;
  serviceRequests: Request[];
  additionalGuests?: BookingGuest[];
  customQuestionAnswers?: ReservationCustomQuestionAnswer[];

  transactions: BillingTransaction[];
}

export interface ReservationCustomQuestionAnswer {
  id: string;
  bookingCheckinId: string;
  questionId?: string | null;
  question: string;
  answer: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReservationStats {
  upSell: number;
  activeReservations: number;
  canceledReservations: number;
  avgLengthOfStay: number;
  noShows: number;
}

export interface ReservationFilters {
  hotelId?: string;
  status?: ReservationStatus[];
  guestStatus?: GuestStatus[];
  country?: string[];
  dateRange?: DateRange;
  search?: string;
  sort?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface CreateReservationRequest {
  hotelId: string;
  roomId: string;
  guestId?: string;
  additionalGuests?: AdditionalGuestDto[];
  checkinStatus: CheckinStatus;
  checkoutStatus: CheckoutStatus;
  checkinMethod: string;
  estimatedTimeOfArrival: string;
  arrivalDate: string;
  departureDate: string;
  preCheckinEnteredGuests: number;
  checkinComments: string;
  notes: string[];
  guest: {
    photoId: string | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    hotelId: string;
    dateOfBirth: string;
    language: string;
    preferences: object;
  };
}

export interface AppendBookingNoteDto {
  content: string;
}

export interface UpdateReservationRequest {
  language?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  dateOfBirth?: string;
  photoId?: string | null;

  hotelId?: string; // UUID
  roomId?: string; // UUID

  status?: ReservationStatus;

  bookingSource?: string;

  checkinStatus?: CheckinStatus;
  checkinMethod?: "FLIGHT" | string;

  checkoutStatus?: CheckoutStatus;

  estimatedTimeOfArrival?: Date;
  checkinComments?: string;

  preCheckinStatus?: PreCheckStatus;

  preCheckinEnteredGuests?: number;

  arrivalDate?: Date;
  departureDate?: Date;

  additionalGuests?: AdditionalGuestDto[];
}

export interface BulkUpdateReservationsRequest
  extends Pick<
    UpdateReservationRequest,
    "checkinStatus" | "checkoutStatus" | "preCheckinStatus"
  > {
  bookingIds: string[];
}

export interface AdditionalGuestDto {
  guestType: GuestType;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  language?: string;
  dateOfBirth?: string; // ISO date string (YYYY-MM-DD)
}

export interface ReservationResponse {
  data: Reservation[];
  total: number;
  page: number;
  limit: number;
}
